import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Journey, JourneyStep, CompletionMethod, JourneySetup, SkipType } from '../../types/journey';
import { WorkflowDisplay } from '../../components/journey/WorkflowDisplay';
import { useJourneyApi } from '../../hooks/useJourneyApi';
import BasePageLayout from '../../components/BasePageLayout';
import { JourneyPlatformIntegrationService } from '../../services/journeyPlatformIntegrationService';
import { JourneyApiService } from '../../services/journeyApiService';
import { BusinessIdea } from '../../types/ai';
import { AIIdeaGeneratorService } from '../../services/aiIdeaGeneratorService';
import { QuestionnaireService, CreateQuestionnaireInput } from '../../services/questionnaireService';
import { DEFAULT_VALIDATION_TEMPLATE } from '../../types/validation';
import { supabase } from '../../services/supabaseClient';
import { showToast } from '../../utils/toast';
import { useLanguage } from '../../context/LanguageContext';

export const WorkflowDisplayPage: React.FC = () => {
  const { journeyId } = useParams<{ journeyId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getJourney, updateStepStatus, resetJourney } = useJourneyApi();
  const { t } = useLanguage();
  
  const [journey, setJourney] = useState<Journey | null>(null);
  const [steps, setSteps] = useState<JourneyStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  type ValidationSurveyInfo = {
    surveyId: string;
    ideaTitle: string;
    createdAt: string;
  };

  const [selectedIdea, setSelectedIdea] = useState<BusinessIdea | null>(null);
  const [validationSurvey, setValidationSurvey] = useState<ValidationSurveyInfo | null>(null);
  
  type PreReleaseProjectInfo = {
    projectId: string;
    projectTitle: string;
    createdAt: string;
  };
  const [preReleaseProject, setPreReleaseProject] = useState<PreReleaseProjectInfo | null>(null);
  const { uiLanguage } = useLanguage();

  // Helper function to localize step content
  const localizeStep = useCallback((step: JourneyStep): JourneyStep => {
    console.log('🌐 Localizing step:', {
      stepIndex: step.stepIndex,
      uiLanguage,
      title: step.title,
      title_zh: step.title_zh,
      hasChineseTitle: !!step.title_zh,
      hasDodCriteria: !!step.dodCriteria,
      dodCriteriaLength: step.dodCriteria?.length || 0
    });
    
    if (uiLanguage === 'zh') {
      const localized = {
        ...step,
        title: step.title_zh || step.title,
        description: step.description_zh || step.description,
        actions: step.actions.map(action => ({
          ...action,
          text: action.text_zh || action.text,
          description: action.description_zh || action.description,
          helpText: action.helpText_zh || action.helpText,
        })),
        // Translate Definition of Done criteria
        dodCriteria: step.dodCriteria_zh && step.dodCriteria_zh.length > 0 
          ? step.dodCriteria_zh 
          : step.dodCriteria,
      };
      console.log('✅ Localized to Chinese:', {
        originalTitle: step.title,
        localizedTitle: localized.title,
        usedFallback: !step.title_zh,
        dodCriteriaTranslated: !!step.dodCriteria_zh && step.dodCriteria_zh.length > 0
      });
      return localized;
    }
    return step;
  }, [uiLanguage]);

  // Store original (non-localized) steps for re-localization
  const [originalSteps, setOriginalSteps] = useState<JourneyStep[]>([]);

  useEffect(() => {
    const loadJourney = async () => {
      if (!journeyId) {
        setError(t('journey.errorLoadingJourney'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const journeyData = await getJourney(journeyId);
        if (journeyData) {
          setJourney(journeyData.journey);
          // Store original steps for re-localization
          setOriginalSteps(journeyData.steps);
          // Localize steps based on current language
          const localizedSteps = journeyData.steps.map(localizeStep);
          setSteps(localizedSteps);
        } else {
          setError(t('journey.journeyNotFound'));
        }
      } catch (err) {
        console.error('Failed to load journey:', err);
        setError(t('journey.errorLoadingJourney'));
      } finally {
        setLoading(false);
      }
    };

    loadJourney();

    // Removed visibilitychange listener - it was causing unnecessary reloads
    // Journey data should only reload when journeyId changes
    
  }, [journeyId, getJourney, t, localizeStep]);

  // Re-localize steps when language changes (but NOT when originalSteps changes - that would cause infinite loop)
  // We use a ref to track if we've already localized the current set of steps
  const [lastLocalizedLanguage, setLastLocalizedLanguage] = useState(uiLanguage);
  
  useEffect(() => {
    // Only re-localize if language actually changed AND we have original steps
    if (originalSteps.length > 0 && uiLanguage !== lastLocalizedLanguage) {
      console.log('🔄 Re-localizing steps due to language change:', { from: lastLocalizedLanguage, to: uiLanguage });
      const localizedSteps = originalSteps.map(localizeStep);
      setSteps(localizedSteps);
      setLastLocalizedLanguage(uiLanguage);
    }
  }, [uiLanguage]); // Only depend on language change, NOT on localizeStep or originalSteps

  // Restore previously selected idea and validation survey on load
  useEffect(() => {
    if (!journeyId) return;

    const processedIdeaKey = `journey_selected_idea_${journeyId}`;
    const selectedIdeaKey = `selected_idea_${journeyId}`;
    
    // Try both keys - processed first, then original
    const persistedIdea = sessionStorage.getItem(processedIdeaKey) || sessionStorage.getItem(selectedIdeaKey);
    
    if (!selectedIdea) {
      if (persistedIdea) {
        try {
          const parsedIdea: BusinessIdea = JSON.parse(persistedIdea);
          setSelectedIdea(parsedIdea);
          console.log('✅ Restored selected idea from sessionStorage:', parsedIdea.title);
        } catch (parseError) {
          console.warn('❌ Failed to restore persisted idea:', parseError);
        }
      } else {
        console.log('ℹ️ No selected idea found in sessionStorage for journey:', journeyId);
      }
    } else if (!persistedIdea) {
      setSelectedIdea(null);
    }

    const surveyTrackingKey = `validation_survey_${journeyId}`;
    const storedSurvey = sessionStorage.getItem(surveyTrackingKey);
    if (storedSurvey) {
      try {
        const parsedSurvey: ValidationSurveyInfo = JSON.parse(storedSurvey);
        setValidationSurvey(prev => {
          if (prev && prev.surveyId === parsedSurvey.surveyId && prev.createdAt === parsedSurvey.createdAt) {
            return prev;
          }
          return parsedSurvey;
        });
      } catch (parseError) {
        console.warn('Failed to restore validation survey info:', parseError);
      }
    } else {
      setValidationSurvey(prev => (prev ? null : prev));
    }

    // Restore pre-release project info
    const preReleaseTrackingKey = `pre_release_project_${journeyId}`;
    const storedProject = sessionStorage.getItem(preReleaseTrackingKey);
    if (storedProject) {
      try {
        const parsedProject: PreReleaseProjectInfo = JSON.parse(storedProject);
        setPreReleaseProject(prev => {
          if (prev && prev.projectId === parsedProject.projectId && prev.createdAt === parsedProject.createdAt) {
            return prev;
          }
          return parsedProject;
        });
        console.log('✅ Restored pre-release project from sessionStorage:', parsedProject.projectTitle);
      } catch (parseError) {
        console.warn('Failed to restore pre-release project info:', parseError);
      }
    } else {
      setPreReleaseProject(prev => (prev ? null : prev));
    }
  }, [journeyId, selectedIdea]);

  // Handle returning from AI idea generator with selected idea
  useEffect(() => {
    const ideaSelected = searchParams.get('ideaSelected');
    console.log('🔍 Effect triggered - ideaSelected:', ideaSelected, 'journeyId:', journeyId);
    
    if (ideaSelected === 'true' && journeyId) {
      console.log('🔄 Detected return from AI idea generator');
      
      // Retrieve selected idea from sessionStorage
      const selectedIdeaKey = `selected_idea_${journeyId}`;
      const storedIdea = sessionStorage.getItem(selectedIdeaKey);
      console.log('📦 Stored idea data:', storedIdea ? 'found' : 'not found');
      
      if (storedIdea) {
        try {
          const parsedIdea: BusinessIdea = JSON.parse(storedIdea);
          setSelectedIdea(parsedIdea);
          const processedIdeaKey = `journey_selected_idea_${journeyId}`;
          sessionStorage.setItem(processedIdeaKey, storedIdea);
          setValidationSurvey(prev => {
            if (prev && prev.ideaTitle !== parsedIdea.title) {
              const surveyTrackingKey = `validation_survey_${journeyId}`;
              sessionStorage.removeItem(surveyTrackingKey);
              return null;
            }
            return prev;
          });
          console.log('📦 Retrieved selected idea:', parsedIdea.title);
          
          // Clean up URL parameter
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('ideaSelected');
          setSearchParams(newSearchParams, { replace: true });
          
          // Check if journey is loaded, if not, wait for it
          if (!journey) {
            console.log('⏳ Journey not loaded yet, will retry when journey loads');
            return; // The dependency array includes 'journey', so this will re-run when journey loads
          }
          
          // Auto-advance to validation step for skilled-maker journey
          console.log('🔍 Journey persona check:', {
            persona: journey?.persona,
            isSkilleMaker: journey?.persona === 'skilled-maker',
            journeyStage: journey?.stage,
            journeyMarket: journey?.market
          });
          
          // TEMPORARILY REMOVE PERSONA CHECK FOR DEBUGGING
          console.log('🧪 DEBUG: Triggering auto-survey creation for ALL personas');
          console.log('Journey persona:', journey?.persona);
          handleIdeaSelectionAdvancement(parsedIdea);
        } catch (error) {
          console.error('Failed to parse selected idea from sessionStorage:', error);
        }
      } else {
        console.warn('No selected idea found in sessionStorage');
      }
    }
  }, [searchParams, journeyId, journey, setSearchParams]);

  /**
   * Auto-creates validation survey for selected business idea
   */
  const createValidationSurveyForIdea = useCallback(async (idea: BusinessIdea, language: string = 'en') => {
    console.log('🎯 createValidationSurveyForIdea started for:', idea.title);
    console.log('🌐 Survey language:', language);
    try {
      // Get authenticated user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('❌ User not authenticated for survey creation');
        return;
      }
      console.log('✅ User authenticated:', user.id);

            // Get a valid country code from country_settings table
      let countryCode = 'US'; // Default fallback
      
      try {
        // Get any valid country from country_settings table
        const { data: validCountries } = await supabase
          .from('country_settings')
          .select('country_code')
          .eq('is_active', true)
          .limit(1);
        
        if (validCountries && validCountries.length > 0) {
          countryCode = validCountries[0].country_code;
          console.log('✅ Using active country code from settings:', countryCode);
        } else {
          // If no active countries, get any country
          const { data: anyCountries } = await supabase
            .from('country_settings')
            .select('country_code')
            .limit(1);
          
          if (anyCountries && anyCountries.length > 0) {
            countryCode = anyCountries[0].country_code;
            console.log('✅ Using any available country code:', countryCode);
          }
        }
      } catch (error) {
        console.warn('⚠️ Error getting country code from settings, using fallback:', error);
      }

      // Generate AI-powered validation questions with fallback to default template
      let questions;
      try {
        console.log('🤖 Generating AI-powered validation questions in language:', language);
        questions = await AIIdeaGeneratorService.generateValidationQuestions(idea, language);
        console.log('✅ AI-generated questions:', questions);
      } catch (aiError) {
        console.warn('⚠️ AI question generation failed, using default template:', aiError);
        // Use default template questions with idea-specific customization
        questions = DEFAULT_VALIDATION_TEMPLATE.questions.map((q, index) => ({
          ...q,
          id: `default_q${index + 1}`,
          text: q.text.replace(/this type of problem/g, `the problem that ${idea.title} solves`)
            .replace(/this product\/service/g, idea.title)
            .replace(/this solution/g, idea.title)
        }));
      }

      // Create questionnaire input
      const primarySkillRequired = idea.skillsRequired?.find(skill => typeof skill === 'string' && skill.trim().length > 0)?.trim();
      const questionnaireInput: CreateQuestionnaireInput = {
        title: `Would You Use This?: ${idea.title}`,
        description: `Help us validate the business idea "${idea.title}". ${idea.problem}`,
        rewardPoints: 5,
        maxParticipants: 50,
        skillRequired: primarySkillRequired || 'Idea Validation',
        questions: questions.map((q, index) => ({
          question_text: q.text,
          question_type: 'single_choice' as const,
          options: (q.options || []).map((optionText, optionIndex) => ({
            option_text: optionText,
            position: optionIndex
          })),
          position: index
        })),
        publisherId: user.id,
        countryCode: countryCode
      };

      // Create the survey
      console.log('📝 Creating validation survey with questionnaire service...');
      console.log('Survey input:', {
        title: questionnaireInput.title,
        countryCode: questionnaireInput.countryCode,
        publisherId: questionnaireInput.publisherId,
        questionsCount: questionnaireInput.questions.length
      });
      
      const result = await QuestionnaireService.createQuestionnaire(questionnaireInput);
      
      if (result.error) {
        console.error('❌ Survey creation error details:', result.error);
        const errorMessage = typeof result.error === 'string' ? result.error : 
          (result.error as any)?.message || JSON.stringify(result.error);
        throw new Error(`Failed to create validation survey: ${errorMessage}`);
      }

      console.log('🎉 Validation survey created successfully with ID:', result.data?.id);
      
      // Show success notification to user
      showToast(
        `🎉 Validation survey auto-generated for "${idea.title}"! You can now share it to collect feedback.`, 
        'success', 
        { duration: 6000 }
      );
      
      // Store survey ID for tracking (optional - can be used by validation step)
      if (result.data?.id && journeyId) {
        const surveyTrackingKey = `validation_survey_${journeyId}`;
        const surveyInfo: ValidationSurveyInfo = {
          surveyId: result.data.id,
          ideaTitle: idea.title,
          createdAt: new Date().toISOString()
        };
        sessionStorage.setItem(surveyTrackingKey, JSON.stringify(surveyInfo));
        setValidationSurvey(surveyInfo);
      }

      if (result.data?.id) {
        const editUrl = `/questionnaires/${result.data.id}/edit?source=journey`;
        if (typeof window !== 'undefined') {
          const editWindow = window.open(editUrl, '_blank', 'noopener,noreferrer');
          if (!editWindow) {
            navigate(editUrl);
          }
        } else {
          navigate(editUrl);
        }
      }

      return result.data;
    } catch (error) {
      console.error('❌ Failed to create validation survey for idea:', error);
      // Don't throw - let the journey continue even if survey creation fails
    }
  }, [journeyId, navigate]);

  const handleIdeaSelectionAdvancement = useCallback(async (idea: BusinessIdea) => {
    console.log('🚀 handleIdeaSelectionAdvancement called with idea:', idea.title);
    try {
      // Find the validation step (step index 1 for skilled-maker journey, 0-based)
      const validationStep = steps.find(step => step.stepIndex === 1);
      console.log('🔍 Found validation step:', validationStep?.id, 'Total steps:', steps.length);
      
      if (validationStep && journey) {
        console.log('✅ Auto-advancing to validation step with selected idea:', idea.title);
        
        // Mark idea generation step (step index 0) as completed
        const ideaGenerationStep = steps.find(step => step.stepIndex === 0);
        if (ideaGenerationStep && ideaGenerationStep.status !== 'completed') {
          await updateStepStatus({
            stepId: ideaGenerationStep.id,
            status: 'completed',
            reflectionNotes: `Completed idea generation with AI-selected idea: ${idea.title}`
          });
        }

        // Store the selected idea in the journey context for the WorkflowDisplay component
        // This will be passed down to the journey components
        console.log('Selected idea stored and ready for validation step');
        
        // Clean up sessionStorage after successful processing
        const selectedIdeaKey = `selected_idea_${journeyId}`;
        sessionStorage.removeItem(selectedIdeaKey);
        console.log('🧹 Cleaned up sessionStorage');
      } else {
        console.log('❌ Cannot proceed: missing validation step or journey data', {
          hasValidationStep: !!validationStep,
          hasJourney: !!journey,
          stepsCount: steps.length
        });
      }
    } catch (error) {
      console.error('❌ Failed to advance journey after idea selection:', error);
    }
  }, [steps, journey, updateStepStatus, journeyId, createValidationSurveyForIdea]);

  const handleStepAction = async (stepId: string, actionType: string, templateId?: string, ideaFromAction?: BusinessIdea, language: string = 'en') => {
    try {
      if (!journeyId || !journey) {
        console.error('Journey ID and journey data are required for step actions');
        return;
      }
      
      console.log('🌐 Language selected for AI generation:', language);

      console.log('🎯 Step action triggered:', { stepId, actionType, providedTemplateId: templateId });
      console.log('🌟 Journey context:', {
        stage: journey.stage,
        market: journey.market,
        persona: journey.persona,
        journeyId
      });

      // For AI-enhanced pre-release projects, generate content first before navigating
      if (actionType === 'preRelease') {
        const ideaToUse = ideaFromAction || selectedIdea;
        
        if (!ideaToUse) {
          console.error('❌ No idea available for pre-release project generation');
          showToast('Please select a business idea first', 'error');
          return;
        }

        console.log('🤖 Generating AI-enhanced pre-release project...');
        
        // This will be handled by the StepDetailView component with loading state
        // We just need to pass the action through
      }

      // Always use contextual matching - ignore any passed templateId to avoid UUID errors
      // This handles cases where old data might have non-UUID templateId values
      const integrationData = await JourneyPlatformIntegrationService.prepareIntegrationDataWithContext(
        stepId,
        actionType,
        journeyId,
        {
          stage: journey.stage,
          market: journey.market,
          persona: journey.persona,
          actionType: actionType as any
        },
        undefined // Always use contextual matching instead of templateId
      );

      console.log('📦 Integration data prepared:', {
        templateId: integrationData.templateId,
        hasTemplate: !!integrationData.template,
        templateTitle: integrationData.template?.title
      });

      // Generate platform URL with template parameters
      // For AI-enhanced pre-release projects, pass the selected idea
      const ideaToUse = ideaFromAction || selectedIdea;
      
      console.log('🔍 Checking idea for URL generation:', {
        actionType,
        isPreRelease: actionType === 'preRelease',
        hasIdeaFromAction: !!ideaFromAction,
        hasSelectedIdea: !!selectedIdea,
        ideaToUse: ideaToUse ? {
          title: ideaToUse.title,
          problem: ideaToUse.problem?.substring(0, 50)
        } : null
      });
      
      const platformUrl = await JourneyPlatformIntegrationService.generatePlatformUrl(
        integrationData,
        actionType === 'preRelease' ? ideaToUse : undefined
      );

      console.log('🔗 Generated URL:', platformUrl);

      // Navigate to the platform feature with pre-filled template
      navigate(platformUrl);
    } catch (err) {
      console.error('Failed to handle step action:', err);
      // Fallback to basic navigation without template
      const fallbackUrls = {
        survey: '/create-questionnaire',
        task: '/create-task',
        team: '/create-team-request',
        preRelease: '/create-pre-release-project',
        ai: '/journey/ai-idea-generator',
        launchAssets: `/journey/launch-assets-generator?journeyId=${journeyId}`
      };
      
      const fallbackUrl = fallbackUrls[actionType as keyof typeof fallbackUrls];
      if (fallbackUrl) {
        console.log('🔄 Falling back to basic URL:', fallbackUrl);
        navigate(fallbackUrl);
      }
    }
  };

  const handleCompleteStep = async (stepId: string, method: CompletionMethod, reflectionNotes?: string) => {
    try {
      // Use the new validation-enabled completion method
      const result = await JourneyApiService.completeStepWithValidation(stepId, method, reflectionNotes);
      
      console.log('Step completed:', {
        pointsAwarded: result.pointsAwarded,
        nextStepUnlocked: result.nextStepUnlocked,
        validationResults: result.validationResults
      });
      
      // Update state locally instead of reloading entire journey
      // This prevents unnecessary page reloads and improves performance
      setSteps(prevSteps => 
        prevSteps.map(step => {
          if (step.id === stepId) {
            return { ...step, status: 'completed' as const };
          }
          // Unlock next step if applicable
          const completedStep = prevSteps.find(s => s.id === stepId);
          if (result.nextStepUnlocked && completedStep && step.stepIndex === completedStep.stepIndex + 1) {
            return { ...step, status: 'current' as const };
          }
          return step;
        })
      );
      
      showToast(t('journey.stepCompletedSuccess'), 'success');
    } catch (err) {
      console.error('Failed to complete step:', err);
      // Fallback to basic completion if validation fails
      try {
        const result = await updateStepStatus({
          stepId,
          status: 'completed',
          reflectionNotes
        });
        
        // Update state locally for fallback as well
        setSteps(prevSteps => 
          prevSteps.map(step => 
            step.id === stepId ? { ...step, status: 'completed' } : step
          )
        );
      } catch (fallbackErr) {
        console.error('Fallback completion also failed:', fallbackErr);
        showToast(t('journey.stepCompletionFailed'), 'error');
      }
    }
  };

  const handleSkipStep = async (stepId: string, justification: string, skipType: SkipType) => {
    try {
      // Use the enhanced skip functionality
      const result = await JourneyApiService.skipStep(stepId, justification, skipType);
      
      console.log('Step skipped:', {
        step: result.step.title,
        justification: result.justification,
        nextStepUnlocked: result.nextStepUnlocked
      });
      
      // Update state locally instead of reloading entire journey
      setSteps(prevSteps => 
        prevSteps.map(step => {
          if (step.id === stepId) {
            return { ...step, status: 'skipped' as const };
          }
          // Unlock next step if applicable
          const skippedStep = prevSteps.find(s => s.id === stepId);
          if (result.nextStepUnlocked && skippedStep && step.stepIndex === skippedStep.stepIndex + 1) {
            return { ...step, status: 'current' as const };
          }
          return step;
        })
      );
      
      showToast(t('journey.stepSkippedSuccess'), 'success');
    } catch (err) {
      console.error('Failed to skip step:', err);
      showToast(t('journey.stepSkipFailed'), 'error');
    }
  };

  const handleResetJourney = async (newSetup?: JourneySetup, reason?: string, preserveCustomizations?: boolean) => {
    if (!journey) return;
    
    try {
      // Use the enhanced reset functionality
      const result = await JourneyApiService.resetJourney({
        journeyId: journey.id,
        newSetup,
        reason,
        preserveCustomizations
      });
      
      console.log('Journey reset:', {
        newJourneyId: result.journey.id,
        historyEntry: result.historyEntry
      });
      
      // Dispatch custom event to notify other components (like TopNavbar) about the reset
      window.dispatchEvent(new CustomEvent('journeyReset', { 
        detail: { newJourneyId: result.journey.id } 
      }));
      
      navigate(`/journey/${result.journey.id}`);
    } catch (err) {
      console.error('Failed to reset journey:', err);
    }
  };

  const handleJourneyUpdated = (updatedJourney: Journey, updatedSteps: JourneyStep[]) => {
    setJourney(updatedJourney);
    setSteps(updatedSteps);
  };

  // Cleanup sessionStorage on unmount
  useEffect(() => {
    return () => {
      // Clean up any remaining selected idea data when component unmounts
      if (journeyId) {
        const selectedIdeaKey = `selected_idea_${journeyId}`;
        sessionStorage.removeItem(selectedIdeaKey);
      }
    };
  }, [journeyId]);

  if (loading) {
    return (
      <BasePageLayout maxWidth="none">
        <div style={{
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px'
        }}>
          <div>Loading your journey...</div>
        </div>
      </BasePageLayout>
    );
  }

  if (error || !journey) {
    return (
      <BasePageLayout maxWidth="none">
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px',
          gap: '16px'
        }}>
          <div>{error || 'Journey not found'}</div>
          <button 
            onClick={() => navigate('/journey/setup')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Start New Journey
          </button>
        </div>
      </BasePageLayout>
    );
  }

  return (
    <BasePageLayout maxWidth="none">
      <WorkflowDisplay
        journey={journey}
        steps={steps}
        selectedIdea={selectedIdea}
        validationSurvey={validationSurvey}
        preReleaseProject={preReleaseProject}
        onCreateValidationSurveyForIdea={createValidationSurveyForIdea}
        onStepAction={handleStepAction}
        onCompleteStep={handleCompleteStep}
        onSkipStep={handleSkipStep}
        onResetJourney={handleResetJourney}
        onJourneyUpdated={handleJourneyUpdated}
      />
    </BasePageLayout>
  );
};
